import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faEdit, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { EDIT_IMAGE_MODAL } from "../../../../constants/modal.constant";
import { useGlobalCore } from "../../../../cores/globals";
import { useSocialPostCore } from "../../../../cores/socialPost";
import { Position } from "../../../../cores/globals/interfaces/globalEntity";
import { Media } from "../../../../cores/socialPost/interfaces/api/get-social-posts";
import { PostImage } from "../../../../cores/socialPost/interfaces/postImage";
import { ImageEditorForm } from "./ImageEditorForm";
import "./ImagePreview.scss";

interface Props {
  image: PostImage | Media;
}

export const ImagePreview = ({ image }: Props) => {
  const { turnOnModal, turnOffModal } = useGlobalCore();
  const { removeImage } = useSocialPostCore();
  const [name, setName] = useState("");
  const [imageSrc, setImageSrc] = useState("");
  const [showControls, setShowControls] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (image.type) {
      setName((image as PostImage).name);
      setImageSrc((image as PostImage).data);
    } else {
      setName((image as Media).key);
      setImageSrc((image as Media).url);
    }
    // Reset loading state when image changes
    setIsLoading(true);
    setHasError(false);
  }, [image]);

  // Preload image for faster display
  useEffect(() => {
    if (imageSrc) {
      const img = new Image();
      img.onload = () => {
        setIsLoading(false);
        setHasError(false);
      };
      img.onerror = () => {
        setIsLoading(false);
        setHasError(true);
      };
      img.src = imageSrc;
      
      // Set a timeout to show image even if loading takes too long
      const timeout = setTimeout(() => {
        setIsLoading(false);
      }, 2000); // Show image after 2 seconds max
      
      return () => clearTimeout(timeout);
    }
  }, [imageSrc]);

  const handleImageLoad = () => {
    // Image loaded successfully, no need to change state as it's handled in useEffect
  };

  const handleImageError = () => {
    // Image failed to load, no need to change state as it's handled in useEffect
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    turnOnModal(
      EDIT_IMAGE_MODAL,
      <ImageEditorForm
        image={image}
        onClose={() => turnOffModal(EDIT_IMAGE_MODAL)}
      />,
      Position.LEFT
    );
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (image.type) {
      removeImage(image as PostImage);
    }
  };

  const handleImageClick = () => {
    if (image.type) {
      handleEdit({ stopPropagation: () => {} } as React.MouseEvent);
    }
  };

  return (
    <div 
      className="image-preview-container position-relative"
      onMouseEnter={() => setShowControls(true)}
      onMouseLeave={() => setShowControls(false)}
    >
      {/* Loading spinner */}
      {isLoading && (
        <div className="image-loading position-absolute d-flex align-items-center justify-content-center">
          <FontAwesomeIcon 
            icon={faSpinner} 
            spin 
            className="text-muted"
            style={{ fontSize: '1.5rem' }}
          />
        </div>
      )}
      
      {/* Error state */}
      {hasError && (
        <div className="image-error position-absolute d-flex align-items-center justify-content-center">
          <div className="text-center text-muted">
            <FontAwesomeIcon icon={faTimes} className="mb-2" style={{ fontSize: '1.5rem' }} />
            <div style={{ fontSize: '0.8rem' }}>Error al cargar</div>
          </div>
        </div>
      )}
      
      <img
        className={`imagePreview neu-out m-2 pointer ${isLoading ? 'opacity-0' : ''}`}
        src={imageSrc}
        alt={name}
        onClick={handleImageClick}
        onLoad={handleImageLoad}
        onError={handleImageError}
      />
      
      {/* Solo mostrar controles para PostImage (imágenes nuevas) */}
      {image.type && showControls && !isLoading && !hasError && (
        <div className="image-controls position-absolute top-0 end-0 m-2">
          <button
            className="btn btn-sm btn-outline-light me-1"
            onClick={handleEdit}
            title="Editar imagen"
          >
            <FontAwesomeIcon icon={faEdit} />
          </button>
          <button
            className="btn btn-sm btn-outline-danger"
            onClick={handleDelete}
            title="Eliminar imagen"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      )}
    </div>
  );
};
